import { useFloating } from '@floating-ui/react-dom-interactions';
import DayContentWrapper from '@shared/components/DateInput/DayPicker/DayContentWrapper';
import { DayPicker } from '@shared/components/DateInput/DayPicker/DayPicker';
import DayPickerHeader from '@shared/components/DateInput/DayPicker/DayPickerHeader/DayPickerHeader';
import DayPickerWeekdays from '@shared/components/DateInput/DayPicker/DayPickerHeader/DayPickerWeekdays';
import Input from '@shared/components/InputGroup/Input';
import cx from 'classnames';
import { enUS as en, de, lt, da } from 'date-fns/locale';
import moment from 'moment';
import { MouseEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'rooks';

import { ReactComponent as ArrowDown } from 'assets/icons/ArrowDown.svg';
import { ReactComponent as Calendar } from 'assets/icons/Calendar.svg';
import useHideOnOutsideClick from 'hooks/useHideOnOutsideClick';
import i18n from 'i18n/config';
import testingConstants from 'utils/testingConstants';

import { getDefaultDateInputFloatingProps } from './utils';

type DayInputAppearance = 'default' | 'session-timeline';

interface DayInputProps {
  name: string;
  value: Date | undefined | null;
  placeholder: string;
  onChange: (newDate?: Date) => void;
  className?: string;
  disabledBeforeDate?: Date;
  /** Figma 5885:149581 session-filter styling — white field, calendar left, chevron right. */
  appearance?: DayInputAppearance;
}

const iconClassName = 'stroke-1 h-5 w-5 shrink-0 stroke-current cursor-pointer';

const DayInput = ({
  name,
  value,
  onChange,
  placeholder,
  className,
  disabledBeforeDate,
  appearance = 'default',
}: DayInputProps) => {
  const { t } = useTranslation();
  const [isOpen, toggleIsOpen] = useToggle(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const { x, y, reference, floating, strategy } = useFloating(getDefaultDateInputFloatingProps('bottom-start'));

  useHideOnOutsideClick({ ref: pickerRef, onClickOutside: toggleIsOpen, additionalSelector: `#${name}` });

  const handleDatePickerSelect = (newDate?: Date) => {
    onChange(newDate);
    toggleIsOpen();
  };

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onChange(undefined);
    if (isOpen) toggleIsOpen();
  };

  const formattedValue = value ? moment(value).format('yyyy-MM-DD') : '';
  const languages = { en, de, lt, da };
  const currentLanguage = i18n.language as keyof typeof languages;
  const isSessionTimeline = appearance === 'session-timeline';
  const hasValue = !!value;

  const picker = isOpen ? (
    <div
      className="z-10"
      ref={(element) => {
        pickerRef.current = element;
        floating(element);
      }}
      style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
    >
      <DayPicker
        id="day-picker"
        className="bg-white shadow-lg rounded-lg"
        mode="single"
        selected={value ?? undefined}
        onSelect={handleDatePickerSelect}
        locale={languages[currentLanguage]}
        weekStartsOn={1}
        disabled={{ before: disabledBeforeDate, after: moment().add(3, 'months').toDate() }}
        components={{
          DayContent: DayContentWrapper,
          Caption: () => <DayPickerHeader />,
          HeadRow: DayPickerWeekdays,
        }}
      />
    </div>
  ) : null;

  if (isSessionTimeline) {
    return (
      <div className={className}>
        <div
          id={name}
          data-testid={testingConstants.dateInput}
          role="button"
          tabIndex={0}
          ref={(element) => {
            reference(element);
          }}
          className={cx(
            'flex h-10 w-full cursor-pointer items-center gap-2 rounded-xl border bg-white px-3 focus-within:ring-4',
            hasValue ? 'border-primary-50' : 'border-gray-40'
          )}
          onClick={toggleIsOpen}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggleIsOpen();
            }
          }}
        >
          <Calendar className={iconClassName} aria-hidden="true" />
          <input
            readOnly
            value={formattedValue}
            placeholder={placeholder}
            className="min-w-0 flex-1 cursor-pointer truncate border-none bg-transparent text-sm font-light text-black placeholder:text-gray-70 focus:outline-none focus:ring-0"
            tabIndex={-1}
          />
          {hasValue && (
            <button
              type="button"
              data-testid={testingConstants.videoSessionTimelineDateClear}
              className="shrink-0 whitespace-nowrap p-0 font-poppins text-size-xs font-medium leading-normal text-gray-60"
              onClick={handleClear}
            >
              {t('video_hub.session_timeline.clear_date')}
            </button>
          )}
          <ArrowDown className={iconClassName} aria-hidden="true" />
        </div>
        {picker}
      </div>
    );
  }

  return (
    <div className={className}>
      <Input
        id={name}
        value={formattedValue}
        inputSize="md"
        className="truncate rounded-lg !bg-gray-10 text-sm font-light cursor-pointer"
        containerClassName="!border-gray-20 focus-within:!border-primary-50 rounded-2xl text-current"
        variant="white-bg"
        icon={Calendar}
        autoComplete="off"
        iconClassName={iconClassName}
        isRounded
        ref={(element) => {
          reference(element);
        }}
        onContainerClick={toggleIsOpen}
        readOnly
        placeholder={placeholder}
        data-testid={testingConstants.dateInput}
      />
      {picker}
    </div>
  );
};

export default DayInput;
